import React, { useEffect, useState, useCallback } from 'react'
import { Link } from 'react-router-dom'
import {
  FaMapMarkerAlt,
  FaStar,
  FaSearch,
  FaFilter,
  FaChevronLeft,
  FaChevronRight,
} from 'react-icons/fa'
import tourService from '../services/tourService'
import locationService from '../services/locationService'
import { toast, ToastContainer } from 'react-toastify'
import Loader from '../components/Loader'
import 'react-toastify/dist/ReactToastify.css'
import { getTourImage } from '../utils/helpers'
import SEO from '../utils/SEO'
import { generateFAQSchema } from '../utils/structuredData'
import { trackSearch } from '../utils/analytics'
import Button from '../components/ui/Button'
import LocationRequestModal from '../components/LocationRequestModal'

const TOURS_FAQS = [
  {
    question: 'What tour packages are available in Himachal Pradesh?',
    answer:
      'We offer adventure treks, honeymoon packages, family tours, weekend getaways, religious pilgrimages, and customized itineraries across Shimla, Manali, Dharamshala, Spiti Valley, and more.',
  },
  {
    question: 'How much do tour packages cost?',
    answer:
      'Our tour packages start from ₹5,000 per person and vary based on the destination, duration, accommodation type, and activities included. Check individual tour pages for exact pricing.',
  },
  {
    question: 'Do tour packages include meals and transport?',
    answer:
      'Most packages include hotel accommodation and transport. Meal inclusion varies by package — check each tour description for detailed inclusions.',
  },
  {
    question: 'Can I book a tour for a large group?',
    answer:
      'Yes, we accommodate groups of all sizes. Contact us for special group rates and customized itineraries for corporate outings, family reunions, or friend trips.',
  },
]

const Tours = () => {
  const [tours, setTours] = useState([])
  const [loading, setLoading] = useState(true)
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    total: 0,
  })
  const [filters, setFilters] = useState({
    search: '',
    location_id: '',
    category: '',
    difficulty: '',
    minPrice: '',
    maxPrice: '',
    duration: '',
  })
  const [showFilters, setShowFilters] = useState(false)
  const [searchTimeout, setSearchTimeout] = useState(null)
  const [locationsFlat, setLocationsFlat] = useState([])
  const [showLocationRequest, setShowLocationRequest] = useState(false)

  useEffect(() => {
    loadTours()
    locationService
      .getAllLocationsFlat()
      .then((data) => {
        setLocationsFlat(data.locations || [])
      })
      .catch(() => {})
  }, [])

  // Auto-search when user types 3+ characters
  useEffect(() => {
    // Clear existing timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout)
    }

    // Only trigger search if search field has 3+ characters
    if (filters.search.length >= 3) {
      const timeout = setTimeout(() => {
        applyFilters()
      }, 500) // 500ms debounce
      setSearchTimeout(timeout)
    } else if (filters.search.length === 0) {
      // Clear search results when search field is empty
      const timeout = setTimeout(() => {
        applyFilters()
      }, 300)
      setSearchTimeout(timeout)
    }

    // Cleanup
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout)
      }
    }
  }, [filters.search])

  const loadTours = async (filterParams = {}, page = 1) => {
    try {
      setLoading(true)
      const params = {
        ...filterParams,
        page,
        limit: 20,
      }
      const data = await tourService.getAllTours(params)
      setTours(data.tours)
      setPagination({
        page: data.page,
        totalPages: data.totalPages,
        total: data.total,
      })

      // Scroll to top of tours
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } catch (error) {
      toast.error('Error loading tours')
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value,
    })
  }

  const applyFilters = () => {
    const activeFilters = Object.fromEntries(
      Object.entries(filters).filter(([_, value]) => value !== ''),
    )
    if (filters.search) trackSearch(filters.search)
    loadTours(activeFilters, 1) // Reset to page 1 on new search
  }

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > pagination.totalPages) return
    const activeFilters = Object.fromEntries(
      Object.entries(filters).filter(([_, value]) => value !== ''),
    )
    loadTours(activeFilters, newPage)
  }

  const clearFilters = () => {
    setFilters({
      search: '',
      destination: '',
      category: '',
      difficulty: '',
      minPrice: '',
      maxPrice: '',
      duration: '',
    })
    loadTours({}, 1)
  }

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      applyFilters()
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <SEO
        title="Tour Packages - Himachal Pradesh & India"
        description="Browse curated tour packages to Shimla, Manali, Dharamshala, Spiti Valley and more. Adventure treks, honeymoon packages, family tours starting from ₹5,000. Book with Himachal Yatra."
        canonical="/tours"
        keywords="tour packages Himachal Pradesh, Shimla Manali tour, trekking packages, honeymoon packages Himachal, adventure tour India"
        structuredData={generateFAQSchema(TOURS_FAQS)}
      />
      <ToastContainer />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-display font-bold text-gray-900 mb-4">Explore Our Tours</h1>
          <p className="text-gray-600 text-lg">
            Find your perfect adventure from our curated collection
          </p>
        </div>

        {/* Search & Filter Bar */}
        <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-lg border border-white/30 p-6 mb-8">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
                <input
                  type="text"
                  name="search"
                  value={filters.search}
                  onChange={handleFilterChange}
                  onKeyPress={handleKeyPress}
                  placeholder="Search tours, destinations..."
                  className="input-field !pl-10"
                />
              </div>
              {filters.search.length > 0 && filters.search.length < 3 && (
                <p className="text-xs text-gray-500 mt-1">Type at least 3 characters to search</p>
              )}
            </div>

            {/* Filter Toggle */}
            <Button
              variant="secondary"
              icon={<FaFilter />}
              onClick={() => setShowFilters(!showFilters)}
            >
              Filters
            </Button>

            {/* Search Button */}
            <Button onClick={applyFilters}>Search</Button>
          </div>

          {/* Advanced Filters */}
          {showFilters && (
            <div className="mt-6 pt-6 border-t border-white/30">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="label">Location</label>
                  <select
                    name="location_id"
                    value={filters.location_id}
                    onChange={handleFilterChange}
                    className="input-field"
                  >
                    <option value="">All Locations</option>
                    {locationsFlat
                      .filter((l) => l.parent_id !== null)
                      .map((loc) => (
                        <option key={loc.id} value={loc.id}>
                          {loc.parent_name ? `${loc.parent_name} → ${loc.name}` : loc.name}
                        </option>
                      ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => setShowLocationRequest(true)}
                    className="text-xs text-primary-600 hover:text-primary-800 mt-1 underline"
                  >
                    Can&apos;t find your destination? Request one
                  </button>
                </div>

                <div>
                  <label className="label">Category</label>
                  <select
                    name="category"
                    value={filters.category}
                    onChange={handleFilterChange}
                    className="input-field"
                  >
                    <option value="">All Categories</option>
                    <option value="standard">Standard</option>
                    <option value="premium">Premium</option>
                    <option value="luxury">Luxury</option>
                  </select>
                </div>

                <div>
                  <label className="label">Difficulty</label>
                  <select
                    name="difficulty"
                    value={filters.difficulty}
                    onChange={handleFilterChange}
                    className="input-field"
                  >
                    <option value="">All Levels</option>
                    <option value="easy">Easy</option>
                    <option value="moderate">Moderate</option>
                    <option value="hard">Hard</option>
                  </select>
                </div>

                <div>
                  <label className="label">Duration</label>
                  <select
                    name="duration"
                    value={filters.duration}
                    onChange={handleFilterChange}
                    className="input-field"
                  >
                    <option value="">Any Duration</option>
                    <option value="3">1-3 Days</option>
                    <option value="5">4-7 Days</option>
                    <option value="10">7+ Days</option>
                  </select>
                </div>

                <div>
                  <label className="label">Min Price (₹)</label>
                  <input
                    type="number"
                    name="minPrice"
                    value={filters.minPrice}
                    onChange={handleFilterChange}
                    onKeyPress={handleKeyPress}
                    placeholder="0"
                    className="input-field"
                  />
                </div>

                <div>
                  <label className="label">Max Price (₹)</label>
                  <input
                    type="number"
                    name="maxPrice"
                    value={filters.maxPrice}
                    onChange={handleFilterChange}
                    onKeyPress={handleKeyPress}
                    placeholder="100000"
                    className="input-field"
                  />
                </div>

                <div className="flex items-end">
                  <Button variant="ghost" fullWidth onClick={clearFilters}>
                    Clear Filters
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Tours Grid */}
        {loading ? (
          <Loader />
        ) : tours.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">No tours found. Try adjusting your filters.</p>
          </div>
        ) : (
          <>
            <div className="mb-6">
              <p className="text-gray-600">
                Showing <span className="font-semibold">{tours.length}</span> of{' '}
                <span className="font-semibold">{pagination.total}</span> tours
                {pagination.totalPages > 1 && (
                  <span className="ml-2">
                    (Page {pagination.page} of {pagination.totalPages})
                  </span>
                )}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {tours.map((tour) => (
                <Link key={tour.id} to={`/tours/${tour.id}`} className="card group">
                  <div className="relative h-64 overflow-hidden shine-on-hover">
                    <img
                      src={getTourImage(tour)}
                      alt={tour.title}
                      loading="lazy"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="bg-primary-600 text-white px-3 py-1 rounded-full text-xs font-semibold uppercase">
                        {tour.category}
                      </span>
                    </div>
                    <div className="absolute top-4 right-4 bg-white px-3 py-1 rounded-full text-sm font-bold text-primary-600">
                      {tour.location_price_on_request
                        ? 'Price on Request'
                        : `₹${tour.base_price.toLocaleString()}`}
                    </div>
                  </div>

                  <div className="p-6">
                    <div className="flex items-center space-x-2 text-sm text-gray-500 mb-2">
                      <FaMapMarkerAlt />
                      <span>{tour.location_name || tour.destination}</span>
                      <span>•</span>
                      <span>{tour.duration} Days</span>
                      <span>•</span>
                      <span className="capitalize">{tour.difficulty}</span>
                    </div>

                    <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-primary-600 transition">
                      {tour.title}
                    </h3>

                    <p className="text-gray-600 mb-4 line-clamp-2">{tour.description}</p>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-1">
                        <FaStar className="text-yellow-400" />
                        <span className="font-semibold">{tour.avg_rating || 0}</span>
                        {tour.review_count > 0 ? (
                          <span className="text-gray-500 text-sm">({tour.review_count})</span>
                        ) : (
                          <span className="bg-primary-100 text-primary-700 text-xs font-semibold px-2 py-0.5 rounded-full">
                            New
                          </span>
                        )}
                      </div>
                      <span className="text-primary-600 font-semibold group-hover:underline">
                        See Full Itinerary →
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="flex justify-center items-center space-x-2 mt-12">
                <Button
                  variant="pagination"
                  size="sm"
                  icon={<FaChevronLeft />}
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1 || loading}
                >
                  Previous
                </Button>

                <div className="flex space-x-2">
                  {[...Array(pagination.totalPages)].map((_, idx) => {
                    const pageNum = idx + 1
                    // Show first page, last page, current page, and pages around current
                    if (
                      pageNum === 1 ||
                      pageNum === pagination.totalPages ||
                      (pageNum >= pagination.page - 1 && pageNum <= pagination.page + 1)
                    ) {
                      return (
                        <Button
                          key={pageNum}
                          variant={pageNum === pagination.page ? 'pagination-active' : 'pagination'}
                          size="sm"
                          onClick={() => handlePageChange(pageNum)}
                          disabled={loading}
                        >
                          {pageNum}
                        </Button>
                      )
                    } else if (pageNum === pagination.page - 2 || pageNum === pagination.page + 2) {
                      return (
                        <span key={pageNum} className="px-2 py-2">
                          ...
                        </span>
                      )
                    }
                    return null
                  })}
                </div>

                <Button
                  variant="pagination"
                  size="sm"
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.totalPages || loading}
                >
                  Next
                  <FaChevronRight className="ml-2" />
                </Button>
              </div>
            )}
          </>
        )}
      </div>
      <LocationRequestModal
        isOpen={showLocationRequest}
        onClose={() => setShowLocationRequest(false)}
      />
    </div>
  )
}

export default Tours
